import { CommonModule } from '@angular/common';
import { Component, computed, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthFestiveDecoration } from '@app/shared/components/auth-festive-decoration/auth-festive-decoration';
import { FestivalDecoration, FestivalType, getActiveFestivalType, shouldHideLoginIllustrations } from '@app/shared/components/festival-decoration/festival-decoration';
import { Spinner } from '@app/shared/components/spinner/spinner';
import { AuthService } from '../../core/services/Auth/auth-service';
import { ValidationConstants } from '../../constants/validation.constants';
import { ToastrService } from 'ngx-toastr';




@Component({
  selector: 'app-forgotpassword',
  imports: [ReactiveFormsModule, CommonModule, RouterModule, AuthFestiveDecoration, FestivalDecoration, Spinner],
  templateUrl: './forgotpassword.html',
  styleUrl: './forgotpassword.scss'
})
export class Forgotpassword implements OnInit {

  // Signals for state management
  private readonly _isLoading = signal<boolean>(false);
  private readonly _errorMessage = signal<string>('');
  private readonly _showOtpField = signal<boolean>(false);
  private readonly _showPasswordField = signal<boolean>(false);
  private readonly _showPassword = signal<boolean>(false);
  private readonly _showConfirmPassword = signal<boolean>(false);
  private readonly _entryPoint = signal('left');
  private readonly _contact = signal<string>('');
  
  // New signals for tracking typing states (Angular 20 features)
  private readonly _isEmailTyping = signal<boolean>(false);
  private readonly _isOtpTyping = signal<boolean>(false);
  private readonly _isPasswordTyping = signal<boolean>(false);
  private readonly _isConfirmPasswordTyping = signal<boolean>(false);
  /** Hide bike/moon illustrations during event windows that replace the login background. */
  readonly hideLoginIllustrations = shouldHideLoginIllustrations();
  private readonly activeFestival = getActiveFestivalType();
  readonly fallOfSummer = this.activeFestival === FestivalType.AUTUMN;
  readonly thanksgiving = this.activeFestival === FestivalType.THANKSGIVING;
  readonly halloween = this.activeFestival === FestivalType.HALLOWEEN;
  readonly remembrance = this.activeFestival === FestivalType.REMEMBRANCE;
  readonly loginHoneybees = [
    { left: '6%', top: '64%', size: '58px', duration: '7.2s', delay: '0s', path: 'down' },
    { left: '22%', top: '74%', size: '46px', duration: '8s', delay: '0.8s', path: 'right' },
    { left: '40%', top: '68%', size: '52px', duration: '6.6s', delay: '1.4s', path: 'left' },
  ] as const;

  // Computed values
  readonly isLoading = computed(() => this._isLoading());
  readonly errorMessage = computed(() => this._errorMessage());
  readonly showOtpField = computed(() => this._showOtpField());
  readonly showPasswordField = computed(() => this._showPasswordField());
  readonly showPassword = computed(() => this._showPassword());
  readonly showConfirmPassword = computed(() => this._showConfirmPassword());
  readonly entryPoint = computed(() => this._entryPoint());
  readonly currentYear = new Date().getFullYear();
  readonly contact = computed(() => this._contact());

  readonly stepTitle = computed(() => {
    if (this.showPasswordField()) {
      return 'Create a New Password';
    }
    if (this.showOtpField()) {
      return 'Enter verification code';
    }
    return 'Forgot password?';
  });

  readonly stepMessage = computed(() => {
    if (this.showPasswordField()) {
      return 'For your security, please set a New Password for your WorkLuge account. Your verification code has been successfully confirmed.';
    }
    if (this.showOtpField()) {
      const destination = this.maskContact(this.contact());
      return `We sent a 4-digit code to ${destination}. Enter it below to continue.`;
    }
    return "Enter your email or phone number and we'll send you a verification code.";
  });
  
  // New computed values for typing states
  readonly isEmailTyping = computed(() => this._isEmailTyping());
  readonly isOtpTyping = computed(() => this._isOtpTyping());
  readonly isPasswordTyping = computed(() => this._isPasswordTyping());
  readonly isConfirmPasswordTyping = computed(() => this._isConfirmPasswordTyping());

  // Form groups
  forgotPasswordForm!: FormGroup;
  otpForm!: FormGroup;
  newPasswordForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private authservice: AuthService,
    private router: Router,
     private toastr: ToastrService

  ) {
    this.initializeForms();
  }

  private initializeForms() {
    // Initialize forgot password form
    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, ValidationConstants.emailOrPhoneValidator()]]
    });

    // Initialize OTP form
    this.otpForm = this.fb.group({
      otp1: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      otp2: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      otp3: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      otp4: ['', [Validators.required, Validators.pattern('^[0-9]$')]]
    });

    // Initialize new password form with password matching validation
    this.newPasswordForm = this.fb.group({
      password: ['', [
        Validators.required,
        Validators.pattern(ValidationConstants.PASSWORD_PATTERN)
      ]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit() {
    const entryPoints = ['left', 'right', 'top', 'bottom'] as const;
    this._entryPoint.set(entryPoints[Math.floor(Math.random() * entryPoints.length)]);
  }

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value ?? '';
    const confirmPassword = group.get('confirmPassword')?.value ?? '';
    if (!confirmPassword) {
      return null;
    }
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  onOtpInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

     this.clearOtpBackendErrors();
    
    // Clear backend error from OTP field
    const otpControl = this.otpForm.get(`otp${index + 1}`);
    if (otpControl?.errors?.['backendError']) {
      otpControl.setErrors(null);
    }
    
    this._isOtpTyping.set(true);

    if (value.length === 1 && index < 3) {
      const nextInput = input.nextElementSibling as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && index > 0) {
      const input = event.target as HTMLInputElement;
      if (!input.value) {
        const prevInput = input.previousElementSibling as HTMLInputElement;
        if (prevInput) {
          prevInput.focus();
        }
      }
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;
    
    const pastedData = clipboardData.getData('text');
    if (!pastedData) return;
    
    // Clean the pasted data to only include digits
    const cleanData = pastedData.replace(/\D/g, '').slice(0, 4);
    
    if (cleanData.length === 4) {
      this.clearOtpBackendErrors();
      // Distribute the pasted OTP across all 4 input fields
      for (let i = 0; i < 4; i++) {
        const otpControl = this.otpForm.get(`otp${i + 1}`);
        if (otpControl) {
          otpControl.setValue(cleanData[i]);
        }
      }
      
      // Focus the last input field
      const lastInput = event.target as HTMLInputElement;
      const inputs = lastInput.parentElement?.querySelectorAll('input[type="text"]') as NodeListOf<HTMLInputElement>;
      if (inputs && inputs.length > 0) {
        inputs[inputs.length - 1].focus();
      }
    }
  }

  togglePasswordVisibility() {
    this._showPassword.update(value => !value);
  }

  toggleConfirmPasswordVisibility() {
    this._showConfirmPassword.update(value => !value);
  }

  /**
   * Converts input to lowercase for email field
   */
  onEmailInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Clear backend error when user starts typing
    if (this.errorMessage()) {
      this._errorMessage.set('');
    }

    // Clear backend error from email field
    const emailControl = this.forgotPasswordForm.get('email');
    if (emailControl?.errors?.['backendError']) {
      const currentErrors = emailControl.errors;
      if (currentErrors) {
        delete currentErrors['backendError'];
        delete currentErrors['backendErrorMessage'];
        if (Object.keys(currentErrors).length === 0) {
          emailControl.setErrors(null);
        } else {
          emailControl.setErrors(currentErrors);
        }
      }
    }

    this._isEmailTyping.set(true);

    // Only lowercase email input — preserve phone numbers with country codes
    if (value.includes('@')) {
      const lowercaseValue = value.toLowerCase();
      if (value !== lowercaseValue) {
        input.value = lowercaseValue;
        emailControl?.setValue(lowercaseValue, { emitEvent: false });
      }
    }
  }

  onEmailBlur() {
    this._isEmailTyping.set(false);
  }

  // Handle email field focus specifically
  onEmailFocus() {
    if (this.errorMessage()) {
      this._errorMessage.set('');
    }
    
    // Clear backend error from email field
    const emailControl = this.forgotPasswordForm.get('email');
    if (emailControl?.errors?.['backendError']) {
      const currentErrors = emailControl.errors;
      if (currentErrors) {
        delete currentErrors['backendError'];
        delete currentErrors['backendErrorMessage'];
        // If no errors left, clear all errors
        if (Object.keys(currentErrors).length === 0) {
          emailControl.setErrors(null);
        } else {
          emailControl.setErrors(currentErrors);
        }
      }
    }
    
   
  }

  // Handle password field focus specifically
  onPasswordFocus() {
    this.clearNewPasswordServerError();
    this._isPasswordTyping.set(true);
    this.newPasswordForm.get('password')?.markAsTouched();
  }

  // Handle confirm password field focus specifically
  onConfirmPasswordFocus() {
    this.clearNewPasswordServerError();
    this._isConfirmPasswordTyping.set(true);
    this.newPasswordForm.get('confirmPassword')?.markAsTouched();
  }

  // Handle password input
  onPasswordInput() {
    this.clearNewPasswordServerError();
    this._isPasswordTyping.set(true);
  }

  // Handle confirm password input
  onConfirmPasswordInput() {
    this.clearNewPasswordServerError();
    this._isConfirmPasswordTyping.set(true);
  }

  private clearNewPasswordServerError(): void {
    if (this.errorMessage()) {
      this._errorMessage.set('');
    }
    this.clearControlBackendError(this.newPasswordForm.get('password'));
    this.clearControlBackendError(this.newPasswordForm.get('confirmPassword'));
  }

  private clearControlBackendError(control: AbstractControl | null): void {
    if (!control?.errors?.['backendError']) {
      return;
    }

    const { backendError, backendErrorMessage, ...remainingErrors } = control.errors;
    void backendError;
    void backendErrorMessage;
    control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  sendOtp() {
    if (this.forgotPasswordForm.valid) {
      this._isLoading.set(true);
      this._errorMessage.set('');
      this._isEmailTyping.set(false);
      
      const contact = this.forgotPasswordForm.get('email')?.value;
      this._contact.set(contact);
      
      this.authservice.sendOTP(contact).subscribe({
        next: () => {
          this._isLoading.set(false);
          this._errorMessage.set('');
          this._showOtpField.set(true);
          this.toastr.success('OTP sent successfully');
        },
        error: (error) => {
          console.error('Error sending OTP:', error);
          this._isLoading.set(false);
          const errorMsg = error.error?.message || 'Sending OTP failed. Please try again.';
          this._errorMessage.set(errorMsg);
          
          // Set backend error on email field
          const emailControl = this.forgotPasswordForm.get('email');
          if (emailControl) {
            // Handle backend errors for contact field (which maps to email field)
            if (error.error?.errors && Array.isArray(error.error.errors)) {
              const contactError = error.error.errors.find((err: any) => err.field === 'contact');
              if (contactError) {
                emailControl.setErrors({ 
                  'backendError': true,
                  'backendErrorMessage': contactError.message 
                });
              } else {
                // If no specific field error, use the main message
                emailControl.setErrors({ 
                  'backendError': true,
                  'backendErrorMessage': errorMsg
                });
              }
            } else if (error.error?.message) {
              // Handle direct message in error response
              emailControl.setErrors({ 
                'backendError': true,
                'backendErrorMessage': error.error.message
              });
            } else {
              emailControl.setErrors({ 
                'backendError': true,
                'backendErrorMessage': errorMsg
              });
            }
            emailControl.markAsTouched();
          }
        }
      });
    } else {
      this._isEmailTyping.set(false);
      Object.keys(this.forgotPasswordForm.controls).forEach(key => {
        const control = this.forgotPasswordForm.get(key);
        control?.markAsTouched();
      });
    }
  }

  verifyOtp() {
    if (this.otpForm.valid) {
      this._isLoading.set(true);
      this._errorMessage.set('');
      this._isOtpTyping.set(false);
      
      const otp = Object.values(this.otpForm.value).join('');
      const contact = this._contact();
      
      this.authservice.verifyOTP({contact,otp}).subscribe({
        next: (response:any) => {
          this._isLoading.set(false);
          this._showOtpField.set(false);
          this._errorMessage.set('');
          this._showPasswordField.set(true);
          this.toastr.success(response.message);
        },
        error: (error:any) => {
          this._isLoading.set(false);
          this._errorMessage.set(error.error.message || 'OTP Verification Failed. Please try again.');
          
          // Set backend error on OTP fields
          for (let i = 1; i <= 4; i++) {
            const otpControl = this.otpForm.get(`otp${i}`);
            if (otpControl) {
              otpControl.setErrors({ 'backendError': true });
              otpControl.markAsTouched();
            }
          }
        }
      });
    } else {
      Object.keys(this.otpForm.controls).forEach(key => {
        const control = this.otpForm.get(key);
        control?.markAsTouched();
      });
    }
  }

  resetPassword() {
    if (this.newPasswordForm.valid) {
      this._isLoading.set(true);
      this._errorMessage.set('');
      this._isPasswordTyping.set(false);
      this._isConfirmPasswordTyping.set(false);
      
      const { password } = this.newPasswordForm.value;
      const contact = this._contact();
      
      this.authservice.resetPassword({contact, password}).subscribe({
        next: (response:any) => {
          this._isLoading.set(false);
          this._errorMessage.set('');
          this.toastr.success(response.message);
          this.router.navigate(['/login-new']);
        },
        error: (error:any) => {
          this._isLoading.set(false);
          const errorMsg = error.error?.message || 'Password reset failed. Please try again.';
          this._errorMessage.set(errorMsg);

          const passwordControl = this.newPasswordForm.get('password');
          const confirmPasswordControl = this.newPasswordForm.get('confirmPassword');
          const backendErrors = {
            backendError: true,
            backendErrorMessage: errorMsg,
          };

          if (passwordControl) {
            passwordControl.setErrors({ ...passwordControl.errors, ...backendErrors });
            passwordControl.markAsTouched();
          }

          if (confirmPasswordControl) {
            confirmPasswordControl.setErrors({ ...confirmPasswordControl.errors, ...backendErrors });
            confirmPasswordControl.markAsTouched();
          }
        }
      });
    } else {
      Object.keys(this.newPasswordForm.controls).forEach(key => {
        const control = this.newPasswordForm.get(key);
        control?.markAsTouched();
      });
    }
  }

  getErrorMessage(controlName: string, formGroup: FormGroup): string {
    const control = formGroup.get(controlName);
    
    // Handle backend errors first
    if (control?.errors?.['backendError']) {
      // Check if there's a specific backend error message stored
      if (control.errors['backendErrorMessage']) {
        return control.errors['backendErrorMessage'];
      }
      // Return generic backend error message
      return 'An error occurred. Please try again.';
    }
    
    if (controlName === 'email') {
      if (control?.errors?.['required'] && control.touched) {
        return 'Please enter Email or Phone';
      }
      return ValidationConstants.getEmailOrPhoneErrorMessage(control!, this.isEmailTyping());
    }
    
    if (controlName === 'confirmPassword') {
      if (control?.errors?.['required'] && (control.touched || this.isConfirmPasswordTyping())) {
        return 'Please confirm the New Password';
      }
      if (formGroup.errors?.['passwordMismatch'] && control?.touched && !control?.errors?.['required']) {
        return 'Passwords do not match';
      }
    }

    if (!control?.errors || !control.touched) return '';

    if (controlName === 'password') {
      if (control.errors['required']) return 'Please enter New Password';
      if (control.errors['pattern']) {
        return 'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
      }
    }

    if (controlName === 'confirmPassword' && control.errors['required']) {
      return 'Please confirm the New Password';
    }

    return '';
  }

  showEmailFieldError(): boolean {
    return ValidationConstants.shouldShowEmailOrPhoneFieldError(this.email, this.isEmailTyping());
  }

  // Helper getters for form validation
  get email() { return this.forgotPasswordForm.get('email'); }
  get password() { return this.newPasswordForm.get('password'); }
  get confirmPassword() { return this.newPasswordForm.get('confirmPassword'); }

  maskContact(contact: string): string {
    const value = contact?.trim();
    if (!value) {
      return 'your registered contact';
    }

    if (value.includes('@')) {
      const [localPart, domain] = value.split('@');
      if (!localPart || !domain) {
        return value;
      }

      if (localPart.length <= 2) {
        return `${localPart[0] ?? '*'}*@${domain}`;
      }

      return `${localPart[0]}${'*'.repeat(Math.min(localPart.length - 2, 4))}${localPart[localPart.length - 1]}@${domain}`;
    }

    const digits = value.replace(/\D/g, '');
    if (digits.length >= 4) {
      return `***${digits.slice(-4)}`;
    }

    return value;
  }

  private clearOtpBackendErrors(): void {
    for (let i = 1; i <= 4; i++) {
      const otpControl = this.otpForm.get(`otp${i}`);
      if (!otpControl?.errors?.['backendError']) {
        continue;
      }

      const { backendError, ...remainingErrors } = otpControl.errors;
      otpControl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
      void backendError;
    }
  }
}
